/**
 * AdminAdsManager — /admin/ads
 * Visually place AdSense snippets, promo images, and sponsored links
 * onto specific pages/slots without touching code.
 *
 * All ads live in the same `content_photos` collection as user photos,
 * distinguished by `added_by_admin=true` (and often `ad_code` populated).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Megaphone, Loader2, ArrowLeft, Plus, Trash2, Image as ImageIcon, Code, Link as LinkIcon, X, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const TARGETS = [
  { key: "general",         label: "Global (site-wide)" },
  { key: "article",         label: "Article" },
  { key: "travel_operator", label: "Travel operator" },
  { key: "business",        label: "Business page" },
  { key: "location",        label: "Location page" },
  { key: "ad",              label: "Ad-only surface" },
];
const SLOTS = ["header", "top", "middle", "bottom", "sidebar", "inline"];
const MAX_BYTES = 6 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.slice(r.result.indexOf(",") + 1));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AdminAdsManager() {
  const { user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [mode, setMode] = useState("image");   // 'image' | 'adsense' | 'link'
  const [f, setF] = useState({
    target_type: "general", target_slug: "", target_id: "", slot: "sidebar",
    title: "", description: "", tags: "", credit_name: "Sponsored",
    link_url: "", ad_code: "",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const load = async () => {
    setLoading(true);
    try {
      // Reuse the public list endpoint, then filter by any ad marker
      const r = await axios.get(`${API}/api/photos?target_type=ad&limit=100`);
      // Also list `general`-slot admin adds
      const r2 = await axios.get(`${API}/api/photos?limit=100`);
      const all = [...(r.data.items || []), ...(r2.data.items || [])];
      const seen = new Set();
      const filtered = all.filter(p => {
        if (!p.id || seen.has(p.id)) return false;
        seen.add(p.id);
        return p.ad_code || p.link_url || p.added_by_admin;
      });
      setItems(filtered);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!user?.is_admin && user?.role !== "admin") {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center">
        <h1 className="font-display text-3xl mb-2">Admin only</h1>
        <Link to="/" className="wofy-link">← Home</Link>
      </div>
    );
  }

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!ALLOWED.includes(f.type)) return toast.error("JPG/PNG/WEBP/GIF only");
    if (f.size > MAX_BYTES)         return toast.error("Max 6 MB");
    setFile(f);
    const r = new FileReader(); r.onload = () => setPreview(r.result); r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Title required");
    if (mode === "image" && !file)    return toast.error("Image required");
    if (mode === "adsense" && !f.ad_code.trim()) return toast.error("Ad HTML/JS snippet required");
    if (mode === "link" && !f.link_url.trim())   return toast.error("Link URL required");
    setSaving(true);
    try {
      const payload = {
        target_type: f.target_type,
        target_slug: f.target_slug.trim() || null,
        target_id:   f.target_id.trim() || null,
        slot:        f.slot,
        title:       f.title.trim(),
        description: f.description.trim() || null,
        tags:        f.tags.split(",").map(x => x.trim()).filter(Boolean),
        credit_name: f.credit_name.trim() || "Sponsored",
        link_url:    (mode === "link" || mode === "image") ? (f.link_url.trim() || null) : null,
        ad_code:     mode === "adsense" ? f.ad_code : null,
      };
      if (mode === "image" && file) {
        payload.mime_type   = file.type;
        payload.data_base64 = await fileToBase64(file);
      }
      await axios.post(`${API}/api/photos/admin/add`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Placed on the site");
      setShowForm(false); setFile(null); setPreview(null);
      setF({ ...f, title: "", description: "", tags: "", link_url: "", ad_code: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this ad from the site?")) return;
    try {
      await axios.delete(`${API}/api/photos/${id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Removed");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4" data-testid="admin-ads">
      <Link to="/" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline text-xs opacity-70">ADMIN · MONETIZATION</div>
          <h1 className="font-display text-3xl flex items-center gap-2"><Megaphone className="w-7 h-7" />Ads & Sponsorships</h1>
          <p className="text-sm opacity-70 mt-1">Place AdSense code, promo images, or sponsored links onto specific pages and slots. No code changes required.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="new-ad-btn">
          {showForm ? <><X className="w-4 h-4" />Close</> : <><Plus className="w-4 h-4" />New placement</>}
        </button>
      </div>

      {showForm && (
        <section className="wofy-card !p-5 mb-6" data-testid="ad-form">
          {/* Mode toggle */}
          <div className="flex gap-1 mb-4 flex-wrap">
            {[
              { key: "image",   icon: ImageIcon, label: "Promo image" },
              { key: "adsense", icon: Code,      label: "AdSense / HTML code" },
              { key: "link",    icon: LinkIcon,  label: "Sponsored link" },
            ].map(m => (
              <button key={m.key} onClick={() => setMode(m.key)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs md:text-sm ${mode === m.key ? "text-white" : ""}`}
                style={{ background: mode === m.key ? "var(--wofy-primary)" : "var(--wofy-surface)" }}
                data-testid={`mode-${m.key}`}
              >
                <m.icon className="w-3.5 h-3.5" />{m.label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1">Target *</label>
                <select value={f.target_type} onChange={set("target_type")} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="ad-target-type">
                  {TARGETS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Slot *</label>
                <select value={f.slot} onChange={set("slot")} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="ad-slot">
                  {SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={f.target_slug} onChange={set("target_slug")} placeholder="Target slug (e.g. 'port-of-los-angeles', 'kerala', blank = all)" className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="ad-target-slug" />
              <input value={f.target_id} onChange={set("target_id")} placeholder="Target ID (optional)" className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="ad-target-id" />
            </div>
            <input required value={f.title} onChange={set("title")} placeholder="Title / campaign name *" className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="ad-title" />
            <textarea rows={2} value={f.description} onChange={set("description")} placeholder="Description (visible to admin only)" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="ad-description" />
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={f.tags} onChange={set("tags")} placeholder="Tags (comma-separated)" className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              <input value={f.credit_name} onChange={set("credit_name")} placeholder='Credit / label (e.g. "Sponsored")' className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            </div>

            {mode === "image" && (
              <div>
                <label className="block text-sm font-medium mb-1">Promo image *</label>
                {preview ? (
                  <div className="relative"><img src={preview} alt="" className="max-h-48 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} /><button type="button" onClick={() => { setFile(null); setPreview(null); }} className="absolute top-1 right-1 w-7 h-7 bg-black/60 text-white rounded-full grid place-items-center"><X className="w-3.5 h-3.5" /></button></div>
                ) : (
                  <label className="flex items-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer" style={{ borderColor: "var(--wofy-border)" }}>
                    <ImageIcon className="w-5 h-5 opacity-40" />
                    <span className="text-sm opacity-70">Choose image (JPG/PNG/WEBP, max 6 MB)</span>
                    <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="ad-file" />
                  </label>
                )}
                <input value={f.link_url} onChange={set("link_url")} placeholder="Click-through URL (optional)" className="mt-2 w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="ad-link" />
              </div>
            )}
            {mode === "adsense" && (
              <div>
                <label className="block text-sm font-medium mb-1">Ad HTML / JavaScript snippet *</label>
                <textarea rows={6} value={f.ad_code} onChange={set("ad_code")} placeholder='<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"></script>&#10;<ins class="adsbygoogle" ...></ins>' className="w-full px-3 py-2 rounded-lg border font-mono text-xs" style={{ borderColor: "var(--wofy-border)" }} data-testid="ad-code" />
                <p className="text-xs opacity-60 mt-1">Rendered as-is inside the target slot. Use for Google AdSense, Ezoic, or any third-party ad network.</p>
              </div>
            )}
            {mode === "link" && (
              <div>
                <label className="block text-sm font-medium mb-1">Destination URL *</label>
                <input value={f.link_url} onChange={set("link_url")} placeholder="https://…" className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="ad-link-only" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-full border text-sm" style={{ borderColor: "var(--wofy-border)" }}>Cancel</button>
              <button type="submit" disabled={saving} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="ad-save">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Placing…</> : <><Plus className="w-4 h-4" />Place ad</>}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Placed items */}
      {loading ? (
        <div className="opacity-60 text-center py-10"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="wofy-card !p-10 text-center opacity-70">No ads placed yet — click "New placement" to start.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {items.map(p => (
            <div key={p.id} className="wofy-card !p-4 flex gap-3" data-testid={`ad-${p.id}`}>
              <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0" style={{ background: "var(--wofy-surface)" }}>
                {p.data_uri ? <img src={p.data_uri} alt="" className="w-full h-full object-cover" /> :
                  p.ad_code ? <div className="w-full h-full grid place-items-center"><Code className="w-8 h-8 opacity-40" /></div> :
                  <div className="w-full h-full grid place-items-center"><LinkIcon className="w-8 h-8 opacity-40" /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{p.title}</div>
                <div className="text-xs opacity-70">
                  <code>{p.target_type}</code> → <code>{p.target_slug || p.target_id || "(any)"}</code> · slot=<code>{p.slot}</code>
                </div>
                {p.link_url && <a href={p.link_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5 mt-1"><ExternalLink className="w-3 h-3" />{p.link_url.slice(0, 40)}</a>}
                {p.description && <div className="text-xs opacity-70 mt-1 line-clamp-2">{p.description}</div>}
                <div className="mt-2">
                  <button onClick={() => remove(p.id)} className="text-xs inline-flex items-center gap-1 text-red-600 hover:underline" data-testid={`ad-delete-${p.id}`}>
                    <Trash2 className="w-3 h-3" />Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
